import type { ID } from "@/lib/catalog/types";
import { api } from "@/lib/apiClient";
import { isAxiosError } from "axios";

export type AttendanceMode = "shift" | "roster" | "flexible_hours" | "manual";

export const ATTENDANCE_MODE_OPTIONS: Array<{ value: AttendanceMode; label: string }> = [
  { value: "shift", label: "Shift" },
  { value: "roster", label: "Roster" },
  { value: "flexible_hours", label: "Flexible Hours" },
  { value: "manual", label: "Manual" },
];

export function attendanceModeLabel(mode: AttendanceMode | string | null | undefined): string {
  const hit = ATTENDANCE_MODE_OPTIONS.find((o) => o.value === mode);
  return hit?.label ?? "Shift";
}

export function normalizeAttendanceMode(value: unknown): AttendanceMode {
  const v = String(value ?? "").trim().toLowerCase();
  if (v === "roster" || v === "flexible_hours" || v === "manual" || v === "shift") return v;
  return "shift";
}

export type WorkPolicyAttendanceRule = {
  id: string;
  ruleKey: string;
  ruleValue: string;
  dataType: string;
  description: string;
};

export interface WorkPolicyRecord {
  id: string;
  name: string;
  attendanceMode: AttendanceMode;
  isActive: boolean;
  attendanceRules: WorkPolicyAttendanceRule[];
  createdAt: string;
  updatedAt: string;
}

export type AttendanceCatalogItem = {
  code: string;
  title: string;
  description: string;
  type: "attendance" | "leave";
  global_value: unknown;
  data_type: string;
};

export type WorkPolicyLinkedRuleApi = {
  id?: string | number;
  rule_key?: string;
  rule_value?: string;
  data_type?: string;
  description?: string | null;
};

export type WorkPolicyDraft = {
  name: string;
  attendanceMode: AttendanceMode;
  isActive: boolean;
};

const LS_KEY = "inventory.workPolicies.v1";

type WorkPolicyApiRecord = {
  id?: number | string;
  name?: string | null;
  attendance_mode?: string | null;
  attendance_mode_label?: string | null;
  is_active?: boolean | null;
  attendance_rules?: unknown;
  created_at?: string | null;
  updated_at?: string | null;
};

type UpsertApiResponse = { message?: string; data?: WorkPolicyApiRecord };
type DeleteApiResponse = { message?: string };

function dedupeById(items: WorkPolicyRecord[]): WorkPolicyRecord[] {
  const byId = new Map<ID, WorkPolicyRecord>();
  for (const item of items) {
    byId.set(item.id, item);
  }
  return Array.from(byId.values());
}

function str(v: unknown): string {
  if (v == null || v === "") return "";
  return String(v);
}

function extractRowsFromResponse(body: unknown): WorkPolicyApiRecord[] {
  if (Array.isArray(body)) return body as WorkPolicyApiRecord[];
  if (!body || typeof body !== "object") return [];
  const root = body as Record<string, unknown>;
  const fromBlock = (block: unknown): WorkPolicyApiRecord[] | null => {
    if (Array.isArray(block)) return block as WorkPolicyApiRecord[];
    if (block && typeof block === "object" && !Array.isArray(block)) {
      const inner = (block as Record<string, unknown>).data;
      if (Array.isArray(inner)) return inner as WorkPolicyApiRecord[];
    }
    return null;
  };
  for (const key of ["data", "work_policies", "items", "results"]) {
    const hit = fromBlock(root[key]);
    if (hit) return hit;
  }
  return [];
}

function readLastPageFromListBody(body: unknown): number | null {
  if (!body || typeof body !== "object") return null;
  const meta = (body as Record<string, unknown>).meta;
  if (meta && typeof meta === "object" && !Array.isArray(meta)) {
    const lp = (meta as Record<string, unknown>).last_page;
    if (typeof lp === "number" && lp >= 1) return lp;
  }
  return null;
}

function extractFromUpsertBody(body: unknown): WorkPolicyApiRecord | null {
  if (!body || typeof body !== "object") return null;
  const root = body as Record<string, unknown>;
  const data = root.data;
  if (data && typeof data === "object" && !Array.isArray(data)) {
    const block = data as Record<string, unknown>;
    if ("id" in block || typeof block.name === "string") {
      return block as unknown as WorkPolicyApiRecord;
    }
  }
  if ("id" in root && typeof root.name === "string") {
    return root as unknown as WorkPolicyApiRecord;
  }
  return null;
}

function mapAttendanceRules(raw: unknown): WorkPolicyAttendanceRule[] {
  if (!Array.isArray(raw)) return [];
  return raw.map((row) => {
    const r = row as Record<string, unknown>;
    return {
      id: str(r.id),
      ruleKey: str(r.rule_key),
      ruleValue: str(r.rule_value),
      dataType: str(r.data_type) || "string",
      description: r.description != null ? str(r.description) : "",
    };
  });
}

function mapApiRecord(record: WorkPolicyApiRecord): WorkPolicyRecord {
  const created = record.created_at || new Date().toISOString();
  const updated = record.updated_at || created;
  return {
    id: String(record.id ?? ""),
    name: str(record.name),
    attendanceMode: normalizeAttendanceMode(record.attendance_mode),
    isActive: record.is_active !== false,
    attendanceRules: mapAttendanceRules(record.attendance_rules),
    createdAt: created,
    updatedAt: updated,
  };
}

function buildApiPayload(draft: WorkPolicyDraft) {
  return {
    name: draft.name.trim(),
    attendance_mode: normalizeAttendanceMode(draft.attendanceMode),
    is_active: draft.isActive,
  };
}

function load(): WorkPolicyRecord[] {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as WorkPolicyRecord[];
    if (!Array.isArray(parsed)) return [];
    return dedupeById(
      parsed.map((r) => ({
        ...r,
        attendanceMode: normalizeAttendanceMode((r as { attendanceMode?: unknown }).attendanceMode),
        attendanceRules: Array.isArray(r.attendanceRules) ? r.attendanceRules : [],
      })),
    );
  } catch {
    return [];
  }
}

function save(items: WorkPolicyRecord[]) {
  localStorage.setItem(LS_KEY, JSON.stringify(dedupeById(items)));
}

const PAGE_SIZE = 20;

export function workPolicyApiErrorMessage(error: unknown): string {
  if (isAxiosError(error)) {
    const data = error.response?.data as { message?: string; errors?: Record<string, string[]> } | undefined;
    if (data?.errors) {
      const first = Object.values(data.errors).flat()[0];
      if (first) return first;
    }
    if (typeof data?.message === "string") return data.message;
  }
  return "Something went wrong. Please try again.";
}

export function listWorkPolicies(): WorkPolicyRecord[] {
  return load().sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
}

export function getWorkPolicyById(id: ID): WorkPolicyRecord | undefined {
  return load().find((p) => p.id === id);
}

export async function syncWorkPoliciesFromApi(): Promise<WorkPolicyRecord[]> {
  try {
    const collected: WorkPolicyApiRecord[] = [];
    let page = 1;
    let lastPage = 1;
    do {
      const response = await api.get("/api/v1/work-policies", {
        params: { page, per_page: PAGE_SIZE },
      });
      collected.push(...extractRowsFromResponse(response.data));
      lastPage = readLastPageFromListBody(response.data) ?? 1;
      page += 1;
    } while (page <= lastPage && page <= 50);

    const records = dedupeById(collected.map(mapApiRecord).filter((r) => Boolean(r.id)));
    save(records);
    return listWorkPolicies();
  } catch (error) {
    // Important: allow callers to detect permission issues and show PermissionDenied UI.
    if (isAxiosError(error) && error.response?.status === 403) {
      throw error;
    }
    console.error("[WorkPolicies] sync failed", error);
    return listWorkPolicies();
  }
}

export async function createWorkPolicy(draft: WorkPolicyDraft): Promise<{ record: WorkPolicyRecord; message: string }> {
  const items = load();
  const response = await api.post<UpsertApiResponse>("/api/v1/work-policies", buildApiPayload(draft));
  const created = extractFromUpsertBody(response.data);
  if (!created || created.id == null) {
    throw new Error("Invalid create work policy response.");
  }
  const record = mapApiRecord(created);
  const idx = items.findIndex((p) => p.id === record.id);
  if (idx >= 0) items[idx] = record;
  else items.unshift(record);
  save(items);
  return { record, message: response.data?.message || "Work policy created successfully." };
}

export async function updateWorkPolicy(
  id: ID,
  draft: WorkPolicyDraft,
): Promise<{ record: WorkPolicyRecord; message: string } | null> {
  const items = load();
  const idx = items.findIndex((p) => p.id === id);
  if (idx === -1) return null;
  const response = await api.put<UpsertApiResponse>(`/api/v1/work-policies/${id}`, buildApiPayload(draft));
  const updated = extractFromUpsertBody(response.data);
  if (!updated) {
    throw new Error("Invalid update work policy response.");
  }
  const record = mapApiRecord(updated);
  items[idx] = record;
  save(items);
  return { record, message: response.data?.message || "Work policy updated successfully." };
}

export async function deleteWorkPolicy(id: ID): Promise<{ success: boolean; message: string }> {
  const response = await api.delete<DeleteApiResponse>(`/api/v1/work-policies/${id}`);
  const items = load();
  const next = items.filter((p) => p.id !== id);
  const removed = next.length !== items.length;
  if (removed) save(next);
  return {
    success: removed,
    message: response.data?.message || "Work policy deleted successfully.",
  };
}

function parseAttendanceRulesSetupBody(body: unknown): {
  catalog: AttendanceCatalogItem[];
  linked: WorkPolicyLinkedRuleApi[];
} {
  const block = body && typeof body === "object" && "data" in body ? (body as { data?: unknown }).data : body;
  const b = block && typeof block === "object" ? (block as Record<string, unknown>) : {};
  const catalogRaw = Array.isArray(b.catalog) ? b.catalog : [];
  const linkedRaw = Array.isArray(b.linked) ? b.linked : [];
  const catalog: AttendanceCatalogItem[] = catalogRaw.map((row) => {
    const r = row as Record<string, unknown>;
    const typeRaw = str(r.type).toLowerCase();
    return {
      code: str(r.code),
      title: str(r.title),
      description: str(r.description),
      type: typeRaw === "leave" ? "leave" : "attendance",
      global_value: r.global_value,
      data_type: str(r.data_type) || "string",
    };
  });
  return { catalog, linked: linkedRaw as WorkPolicyLinkedRuleApi[] };
}

/** Catalog only (no policy id). Same row shape as setup; linked is always empty. */
export async function fetchAttendanceRulesCatalog(): Promise<{
  catalog: AttendanceCatalogItem[];
  linked: WorkPolicyLinkedRuleApi[];
}> {
  const response = await api.get(`/api/v1/work-policies/attendance-rules-catalog`);
  return parseAttendanceRulesSetupBody(response.data);
}

export async function fetchAttendanceRulesSetup(policyId: ID): Promise<{
  catalog: AttendanceCatalogItem[];
  linked: WorkPolicyLinkedRuleApi[];
}> {
  const response = await api.get(`/api/v1/work-policies/${policyId}/attendance-rules-setup`);
  return parseAttendanceRulesSetupBody(response.data);
}

/** Work policies that are active and have at least one attendance rule (for employee assignment). */
export async function fetchAssignableWorkPoliciesForEmployees(): Promise<Array<{ id: string; name: string }>> {
  const response = await api.get<{ data?: Array<{ id?: number; name?: string }> }>(
    "/api/v1/work-policies/assignable-for-employees",
  );
  const rows = Array.isArray(response.data?.data) ? response.data.data : [];
  return rows
    .map((r) => ({ id: String(r.id ?? ""), name: String(r.name ?? "").trim() }))
    .filter((r) => r.id !== "" && r.name !== "");
}

export async function syncWorkPolicyAttendanceRules(
  policyId: ID,
  rules: Array<{ rule_key: string; rule_value: string; data_type: string; description?: string }>,
): Promise<{ record: WorkPolicyRecord; message: string }> {
  const response = await api.put<{ message?: string; data?: WorkPolicyApiRecord }>(
    `/api/v1/work-policies/${policyId}/attendance-rules`,
    { rules },
  );
  const raw = response.data?.data;
  if (!raw?.id) {
    throw new Error("Invalid sync attendance rules response.");
  }
  const record = mapApiRecord(raw);
  const items = load();
  const idx = items.findIndex((p) => p.id === record.id);
  if (idx >= 0) items[idx] = record;
  else items.unshift(record);
  save(items);
  return { record, message: str(response.data?.message) || "Attendance rules saved." };
}
